import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export const PLAYGROUND_IDS = {
  prompt: "playground-prompt-input",
  model: "playground-model-select",
  generate: "playground-generate-button",
  gallery: "playground-gallery",
  item: "playground-gallery-item",
};

export default function Playground() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("gemini");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [latest, setLatest] = useState(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/image/history");
      setHistory(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (user) loadHistory();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0b0c10]">
        <Navbar />
        <div className="max-w-2xl mx-auto p-10 text-slate-300 text-center">
          Please <Link to="/login" className="underline text-yellow-400">sign in</Link> to use the Playground.
        </div>
      </div>
    );
  }

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/image/generate", { prompt, model }, { timeout: 120000 });
      setLatest(data);
      loadHistory();
      toast.success(`Generated with ${model === "gemini" ? "Nano Banana" : "GPT Image 1"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <p className="telesis-eyebrow">Image Playground</p>
        <h1 className="text-3xl md:text-4xl font-black mt-2 text-slate-100 tracking-tight">
          Summon images from mentation
        </h1>
        <p className="mt-3 text-sm text-slate-400 max-w-2xl">
          Choose between Gemini Nano Banana (fast, illustrative) or OpenAI GPT Image 1 (photorealistic). Both billed to your Emergent LLM key.
        </p>

        <form onSubmit={generate} className="mt-6 telesis-panel rounded p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Prompt</Label>
            <Textarea
              data-testid={PLAYGROUND_IDS.prompt}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              required
              minLength={3}
              maxLength={1500}
              rows={4}
              placeholder="A cathedral of listening, grain of gold, haze of serenity…"
              className="mt-1 bg-[#12141c] border-[#222634] text-slate-100 resize-none"
            />
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Model</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger data-testid={PLAYGROUND_IDS.model} className="mt-1 bg-[#12141c] border-[#222634] text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#12141c] border-[#222634] text-slate-100">
                  <SelectItem value="gemini">Gemini Nano Banana</SelectItem>
                  <SelectItem value="openai">OpenAI GPT Image 1</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid={PLAYGROUND_IDS.generate}
              type="submit"
              disabled={busy || prompt.trim().length < 3}
              className="rounded-full bg-yellow-400 text-black hover:bg-yellow-300 font-semibold px-6"
            >
              {busy ? "Summoning…" : "Generate"}
            </Button>
          </div>
        </form>

        {latest && (
          <section className="mt-8">
            <p className="telesis-eyebrow">Latest</p>
            <img
              src={`${API}/files/${latest.storage_path}`}
              alt=""
              className="mt-3 w-full max-w-xl rounded border border-white/10"
            />
          </section>
        )}

        <section className="mt-10" data-testid={PLAYGROUND_IDS.gallery}>
          <p className="telesis-eyebrow mb-3">Your generations</p>
          {history.length === 0 ? (
            <p className="text-xs text-slate-500 font-mono">no generations yet</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {history.map((h) => (
                <figure
                  key={h.file_id}
                  data-testid={PLAYGROUND_IDS.item}
                  className="telesis-panel rounded overflow-hidden"
                >
                  <img src={`${API}/files/${h.storage_path}`} alt="" className="w-full h-40 object-cover" />
                  <figcaption className="px-3 py-2 text-[10px] font-mono text-slate-400 truncate uppercase tracking-wider">
                    {h.gen_model === "openai" ? "GPT Image 1" : "Nano Banana"} · {h.gen_prompt?.slice(0, 60)}…
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
